// frontend/src/components/Samples/SampleOrderArticleList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sampleOrdersAPI, customersAPI, productionTypesAPI, sampleOrderTypesAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiEye, FiList } from 'react-icons/fi';
import LineOperationsModal from './LineOperationsModal';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import './SampleOrderList.css';
import LineOpsBadges from './LineOpsBadges';

const SampleOrderArticleList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [lineModal, setLineModal] = useState(null);
  const [currentPage, setCurrentPage] = useState(location.state?.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(location.state?.itemsPerPage || 25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  const [filters, setFilters] = useState(location.state?.filters || {
    customerId: '',
    enteredProductCode: '',
    erpProductCode: '',
    erpProductColorCode: '',
    orderDateFrom: '',
    orderDateTo: '',
    deliveryDateFrom: '',
    deliveryDateTo: '',
    sampleOrderNo: '',
    productionTypeId: '',
    sampleOrderTypeId: '',
    status: ''
  });

  const [customers, setCustomers] = useState([]);
  const [productionTypes, setProductionTypes] = useState([]);
  const [sampleOrderTypes, setSampleOrderTypes] = useState([]);

  // Local draft for text fields -- only committed on blur or Enter
  const [textDraft, setTextDraft] = useState({
    sampleOrderNo:       location.state?.filters?.sampleOrderNo       || '',
    enteredProductCode:  location.state?.filters?.enteredProductCode  || '',
    erpProductCode:      location.state?.filters?.erpProductCode      || '',
    erpProductColorCode: location.state?.filters?.erpProductColorCode || '',
  });

  useEffect(() => {
    loadCustomers();
    loadProductionTypes();
    loadSampleOrderTypes();
  }, []);

  useEffect(() => {
    loadArticles();
  }, [currentPage, itemsPerPage, filters]);


  useEffect(() => {
    console.log('[SampleOrderArticleList.jsx] mounted');
    return () => console.log('[SampleOrderArticleList.jsx] unmounted');
  }, []);  


  const loadCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProductionTypes = async () => {
    try {
      const response = await productionTypesAPI.getAll();
      if (response.data.success) {
        setProductionTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error loading production types:', error);
    }
  };

  const loadSampleOrderTypes = async () => {
    try {
      const response = await sampleOrderTypesAPI.getAll();
      if (response.data.success) {
        setSampleOrderTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error loading sample order types:', error);
    }
  };

  const loadArticles = async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(filters.customerId && { customerId: filters.customerId }),
        ...(filters.enteredProductCode && { enteredProductCode: filters.enteredProductCode }),
        ...(filters.erpProductCode && { erpProductCode: filters.erpProductCode }),
        ...(filters.erpProductColorCode && { erpProductColorCode: filters.erpProductColorCode }),
        ...(filters.orderDateFrom && { orderDateFrom: filters.orderDateFrom }),
        ...(filters.orderDateTo && { orderDateTo: filters.orderDateTo }),
        ...(filters.deliveryDateFrom && { deliveryDateFrom: filters.deliveryDateFrom }),
        ...(filters.deliveryDateTo && { deliveryDateTo: filters.deliveryDateTo }),
        ...(filters.sampleOrderNo && { sampleOrderNo: filters.sampleOrderNo }),
        ...(filters.productionTypeId && { productionTypeId: filters.productionTypeId }),
        ...(filters.sampleOrderTypeId && { sampleOrderTypeId: filters.sampleOrderTypeId }),
        ...(filters.status && { status: filters.status })
      });
      
      const response = await sampleOrdersAPI.getSampleOrderArticleList(queryParams.toString());
      
      if (response.data.success) {
        setArticles(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.totalItems);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading articles:', err);
      setError(t('messages.error_load', 'Failed to load articles'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleTextCommit = (field) => {
    if (textDraft[field] !== filters[field]) {
      handleFilterChange(field, textDraft[field]);
    }
  };

  const handleViewOrder = (article) => {
    navigate(`/samples/orders/${article.public_id}`, {
      state: {
        returnTo: '/sample-order-articles',
        returnState: {
          currentPage,
          itemsPerPage,
          filters
        }
      }
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'OPEN': 'status-open',
      'RELEASED': 'status-released',
      'CLOSED': 'status-closed',
      'CANCELLED': 'status-cancelled'
    };
    return statusMap[status] || 'status-open';
  };

  const columns = [
    {
      header: t('sample_orders.order_no', 'Order No'),
      field: 'sample_order_no',
      className: 'code-column'
    },
    {
      header: t('sample_orders.customer', 'Customer'),
      field: 'customer_name'      
    },
    {
      header: t('sample_orders.erp_product', 'ERP Product'),
      field: 'erp_product_code'
    },      
    {
      header: t('sample_orders.erp_color', 'ERP Cor'),
      field: 'erp_product_color_code',
      render: (row) => row.erp_product_color_code || '-'
    },
    {
      header: t('sample_orders.product_code', 'Product'),
      field: 'entered_product_code'
    },
    {
      header: t('sample_orders.order_type', 'Order Type'),
      field: 'sample_order_type'
    },     
   /* {
      header: t('sample_orders.total_quantity', 'Total Qty'),
      field: 'total_quantity',
      className: 'text-right',
      render: (row) => row.total_quantity ? parseFloat(row.total_quantity).toFixed(0) : '0'
    },*/
    {
      header: t('sample_orders.production_type', 'Production Type'),
      field: 'production_type'
    },
    {
      header: t('sample_orders.status', 'Status'),
      field: 'status',
      render: (row) => (
        <span className={`status-badge ${getStatusBadgeClass(row.status)}`}>
          {t(`status.${row.status.toLowerCase()}`, row.status)}
        </span>
      ),
      sortable: false
    },
    {
      header: t('sample_orders.order_date', 'Order Date'),
      field: 'order_date',
      render: (row) => row.order_date ? new Date(row.order_date).toLocaleDateString() : '-'
    },
    {
      header: t('sample_orders.delivery_date', 'Delivery'),
      field: 'requested_delivery_date',
      render: (row) => row.requested_delivery_date ? new Date(row.requested_delivery_date).toLocaleDateString() : '-'
    },
    {
      header: t('actions', 'Actions'),
      field: 'actions',
      sortable: false,
      render: (row) => (
        <div className="action-buttons">

          <button
            className="btn-action"
            title={t('line_operations.view_tasks', 'Ver tarefas da linha')}
            onClick={e => {
              e.stopPropagation();
              setLineModal({
                orderPublicId: row.public_id,
                matrixLineNo:  row.matrix_line_no,
                lineLabel:     row.entered_product_code || row.erp_product_code || row.sample_order_no,
                lineArticle: {
                  enteredProductCode:  row.entered_product_code,
                  enteredProductColor: null,
                  erpProductCode:      row.erp_product_code,
                  erpProductColorCode: row.erp_product_color_code,
                },
                orderDate:      row.order_date,
                orderCreatedAt: row.order_date,
              });
            }}
          >
            <FiList size={16} />
          </button>

          <button
            onClick={e => { e.stopPropagation(); handleViewOrder(row); }}
            className="btn-action btn-view"
            title={t('view', 'View Order')}
          >
            <FiEye size={16} />
          </button>


        </div>
      )
    },
    {
      header: t('sample_orders.line_ops', 'Tarefas'),
      field: 'line_operations',
      sortable: false,
      width: '500px',
      minWidth: '500px',
      render: (row) => (
        <LineOpsBadges operations={row.line_operations} />
      ),
    },


  ];

  return (
    <div className="sample-order-list">
      <div className="page-header">
        <div>
          <h2>{t('sample_orders.article_list', 'Article List')}</h2>
          <p className="page-subtitle">{t('sample_orders.article_subtitle', 'Browse all sample order articles')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="order-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('sample_orders.order_no', 'Order No')}</label>
            <input
              type="text"
              value={textDraft.sampleOrderNo}
              onChange={(e) => setTextDraft(d => ({ ...d, sampleOrderNo: e.target.value }))}
              onBlur={() => handleTextCommit('sampleOrderNo')}
              onKeyDown={(e) => e.key === 'Enter' && handleTextCommit('sampleOrderNo')}
              placeholder={t('search', 'Search...')}
              className="form-control-sm"
            />
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.customer', 'Customer')}</label>
            <select
              value={filters.customerId}
              onChange={(e) => handleFilterChange('customerId', e.target.value)}
            >
              <option value="">{t('all', 'All')}</option>
              {customers.map(customer => (
                <option key={customer.customer_id} value={customer.customer_id}>
                  {customer.customer_code} - {customer.customer_name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.status', 'Status')}</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">{t('all', 'All')}</option>
              <option value="OPEN">{t('status.open', 'Aberto')}</option>
              <option value="RELEASED">{t('status.released', 'Liberto')}</option>
              <option value="CLOSED">{t('status.closed', 'Fechado')}</option>
              <option value="CANCELLED">{t('status.cancelled', 'Cancelado')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.product_code', 'Product Code')}</label>
            <input
              type="text"
              value={textDraft.enteredProductCode}
              onChange={(e) => setTextDraft(d => ({ ...d, enteredProductCode: e.target.value }))}
              onBlur={() => handleTextCommit('enteredProductCode')}
              onKeyDown={(e) => e.key === 'Enter' && handleTextCommit('enteredProductCode')}
              placeholder={t('search', 'Search...')}
              className="form-control-sm"
            />
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.erp_product', 'ERP Product')}</label>
            <input
              type="text"
              value={textDraft.erpProductCode}
              onChange={(e) => setTextDraft(d => ({ ...d, erpProductCode: e.target.value }))}
              onBlur={() => handleTextCommit('erpProductCode')}
              onKeyDown={(e) => e.key === 'Enter' && handleTextCommit('erpProductCode')}
              placeholder={t('search', 'Search...')}
              className="form-control-sm"
            />
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.erp_color', 'ERP Cor')}</label>
            <input
              type="text"
              value={textDraft.erpProductColorCode}
              onChange={(e) => setTextDraft(d => ({ ...d, erpProductColorCode: e.target.value }))}
              onBlur={() => handleTextCommit('erpProductColorCode')}
              onKeyDown={(e) => e.key === 'Enter' && handleTextCommit('erpProductColorCode')}
              placeholder={t('search', 'Search...')}
              className="form-control-sm"
            />
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.order_type', 'Order Type')}</label>
            <select
              value={filters.sampleOrderTypeId}
              onChange={(e) => handleFilterChange('sampleOrderTypeId', e.target.value)}
            >
              <option value="">{t('all', 'All')}</option>
              {sampleOrderTypes.map(sot => (
                <option key={sot.sample_order_type_id} value={sot.sample_order_type_id}>
                  {sot.sample_order_type}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.production_type', 'Production Type')}</label>
            <select
              value={filters.productionTypeId}
              onChange={(e) => handleFilterChange('productionTypeId', e.target.value)}
            >
              <option value="">{t('all', 'All')}</option>
              {productionTypes.map(pt => (
                <option key={pt.production_type_id} value={pt.production_type_id}>
                  {pt.production_type}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.order_date', 'Order Date From')}</label>
            <input
              type="date"
              value={filters.orderDateFrom}
              onChange={(e) => handleFilterChange('orderDateFrom', e.target.value)}
              className="form-control-sm"
            />
          </div>

          <div className="filter-group">
            <label>{t('sample_orders.order_date', 'Order Date To')}</label>
            <input
              type="date"
              value={filters.orderDateTo}
              onChange={(e) => handleFilterChange('orderDateTo', e.target.value)}
              className="form-control-sm"
            />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={articles}
        loading={loading}
        error={error}
        onRetry={loadArticles}
        onRowClick={(row) => handleViewOrder(row)}
        emptyMessage={t('no_data', 'No articles found')}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}
      {/* Line operations modal */}
      {lineModal && (
        <LineOperationsModal
          orderPublicId={lineModal.orderPublicId}
          matrixLineNo={lineModal.matrixLineNo}
          lineLabel={lineModal.lineLabel}
          lineArticle={lineModal.lineArticle}
          orderDate={lineModal.orderDate}
          orderCreatedAt={lineModal.orderCreatedAt}
          defaultFilter="SELECTED"
          onClose={() => setLineModal(null)}
        />
      )}

    </div>
  );
};

export default SampleOrderArticleList;